import React from 'react';
import { FormControl, Grid, MenuItem, Select } from '@mui/material';
import SearchFilter from '../../../components/filters/SearchFilter';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  READ_CUSTOMER,
  READ_PRODUCT,
  READ_PURCHASE_INVOICE,
  READ_SALE_INVOICE,
  READ_SUPPLIER,
} from '../../../constants/eInvoicePermissions';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';

const CATEGORY_OPTION_LABELS = {
  ALL: {
    key: 'all_category',
    fallback: 'All Category',
  },
  SALES_DOCUMENTS: {
    key: 'sales_invoice',
    fallback: 'Sales Documents',
  },
  PURCHASE_DOCUMENTS: {
    key: 'self_billed_invoice',
    fallback: 'Purchase Documents',
  },
  CUSTOMER: {
    key: 'customer',
    fallback: 'Customers',
  },
  SUPPLIER: {
    key: 'supplier',
    fallback: 'Suppliers',
  },
  PRODUCT: {
    key: 'product',
    fallback: 'Products/Services',
  },
};

const BulkUploadListFilters = ({
  filters,
  selectedFilter,
  selectedStatus,
  onFilterChange,
  searchTerm,
  onSearchChange,
  onSearchKeyPress,
  onStatusChange,
  onCategoryChange,
  showCategoryFilter = false,
  selectedCategory = 'ALL',
  categoryFilterOptions = ['ALL', 'SALES_DOCUMENTS', 'PURCHASE_DOCUMENTS', 'CUSTOMER', 'SUPPLIER', 'PRODUCT'],
}) => {
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();

  const renderFilter = (filter) => {
    switch (filter.type) {
      case 'search':
        return (
          <SearchFilter
            key={filter.type}
            filter={filter}
            selectedFilter={selectedFilter}
            onFilterChange={onFilterChange}
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            onSearchKeyPress={onSearchKeyPress}
          />
        );

      default:
        return null;
    }
  };

  const getCategoryPermission = (option) => {
    switch (option) {
      case 'SALES_DOCUMENTS':
        return hasEInvoicePermission(READ_SALE_INVOICE);

      case 'PURCHASE_DOCUMENTS':
        return hasEInvoicePermission(READ_PURCHASE_INVOICE);

      case 'CUSTOMER':
        return hasEInvoicePermission(READ_CUSTOMER);

      case 'SUPPLIER':
        return hasEInvoicePermission(READ_SUPPLIER);

      case 'PRODUCT':
        return hasEInvoicePermission(READ_PRODUCT);

      case 'ALL':
      default:
        return true;
    }
  };

  const handleCategoryChange = (event) => {
    const selectedCategory = event.target.value;

    if (!getCategoryPermission(selectedCategory)) {
      return;
    }

    onCategoryChange(event);
  };

  const renderCategoryOption = (option) => {
    const labelConfig = CATEGORY_OPTION_LABELS[option] ?? {
      fallback: option,
    };

    const hasPermission = getCategoryPermission(option);

    return (
      <MenuItem
        key={option}
        value={option}
        disabled={!hasPermission}
        sx={{
          '&.Mui-disabled': {
            pointerEvents: 'auto',
            cursor: 'not-allowed',
          },
        }}
      >
        <PermissionDeniedWrapper show={!hasPermission} title={accessDeniedMessage} placement="right" fullWidth>
          <span
            style={{
              display: 'block',
              width: '100%',
              cursor: hasPermission ? 'pointer' : 'not-allowed',
            }}
          >
            {labelConfig.fallback}
          </span>
        </PermissionDeniedWrapper>
      </MenuItem>
    );
  };

  return (
    <Grid item xs={12} sx={{ padding: '2px' }} container justifyContent="space-between" alignItems="center" spacing={1}>
      <Grid
        item
        xs={12}
        sx={{
          mb: 2,
          rowGap: '5px',
        }}
        container
        spacing={1}
      >
        <Grid item>
          <FormControl
            sx={{
              padding: '1px',
              minWidth: 140,
            }}
          >
            <Select
              value={selectedStatus}
              onChange={onStatusChange}
              displayEmpty
              size="small"
              sx={{
                fontSize: '0.875rem',
                padding: '1px',
              }}
              variant="outlined"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="UPLOADED">Queued</MenuItem>
              <MenuItem value="PROCESSING">Processing</MenuItem>
              <MenuItem value="COMPLETED">Processed</MenuItem>
              <MenuItem value="UPLOAD_FAILED">Failed</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {showCategoryFilter && (
          <Grid item>
            <FormControl
              sx={{
                padding: '1px',
                minWidth: 180,
                ml: '6px',
              }}
            >
              <Select
                value={selectedCategory}
                onChange={handleCategoryChange}
                displayEmpty
                size="small"
                sx={{
                  fontSize: '0.875rem',
                  padding: '1px',
                }}
                variant="outlined"
              >
                {categoryFilterOptions.map(renderCategoryOption)}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      <Grid item container spacing={1} justifyContent="flex-end">
        {filters.map((filter) => renderFilter(filter))}
      </Grid>
    </Grid>
  );
};

export default BulkUploadListFilters;
